// src/pages/ProfilePage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints } from '../services/api';
import { PRICES } from '../config/pricing.js';
import { FREE_MODE } from '../config/freeMode';
import '../styles/pages/Profile.css';

// ── Static plan data ────────────────────────────────────────────────────────

const PLAN_META = {
  free: {
    label: 'Trial ended',
    features: [
      'Your guides still play and can be shared',
      'Upgrade to create new guides and use the AI assistant',
    ],
  },
  individual: {
    label: 'Individual',
    features: [
      'Unlimited guides',
      'AI chat assistant (300 questions a month)',
      'PDF export without watermark',
      'Share guides publicly or privately',
      'Unique viewers, completion rate & drop-off chart',
      'Play history (last 30 sessions)',
      'Solo creator workspace',
    ],
  },
  teams: {
    label: 'Teams',
    features: [
      'Everything in Individual',
      'Collaborative workspace (5 seats included)',
      'Per-member completion table',
      'Time benchmarking across members',
      'Guide health score',
      '30-day play trend chart',
      'Guide rank within workspace',
    ],
  },
};

function getInitials(user) {
  if (user?.display_name) return user.display_name.charAt(0).toUpperCase();
  if (user?.email) return user.email.charAt(0).toUpperCase();
  return '?';
}

// ── Cancel Modal ─────────────────────────────────────────────────────────────

function CancelModal({ plan, onConfirm, onClose, loading }) {
  return (
    <div className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
      <div className="profile-modal__sheet m-sheet">
        <h2 id="cancel-title" className="profile-modal__title">
          Cancel {PLAN_META[plan]?.label} plan?
        </h2>
        <p className="profile-modal__body">
          Your plan will stay active until the end of your current billing period, then downgrade to{' '}
          <strong>Free</strong>.
          {plan === 'teams' && (
            <> Your workspace will be deleted and all members will be moved to the Free plan.</>
          )}{' '}
          You'll keep your guides but lose access to paid features.
        </p>
        <div className="profile-modal__actions">
          <button className="lp-btn quiet" onClick={onClose} disabled={loading}>
            Keep plan
          </button>
          <button className="profile-danger-btn profile-danger-btn--solid" onClick={onConfirm} disabled={loading}>
            {loading ? 'Cancelling…' : 'Yes, cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { user, token, refreshUser, logout } = useAuth();
  const { subscription } = useAuth();
  const navigate = useNavigate();
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // ── FIX: ProtectedRoute guarantees a token exists before this mounts,
  //         but the user object may still be loading from the auth context.
  //         Show a spinner instead of blank/navigating during render.
  if (!user) {
    return (
      <div className="profile-loading" role="status" aria-label="Loading account">
        <span className="profile-spinner" />
      </div>
    );
  }

  const plan = user.plan || 'free';
  const meta = PLAN_META[plan] || PLAN_META.free;

  // /me reports a trial as "individual" and sets trial_ends_at only for unpaid accounts
  const onTrial = plan === 'individual' && !!user.trial_ends_at;
  const trialEnd = onTrial && new Date(user.trial_ends_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const isPaid = plan !== 'free' && !onTrial;
  const isRazorpay = subscription?.gateway === 'razorpay';
  const currencyPrices = isRazorpay ? PRICES.inr : PRICES.usd;
  const planPrices = currencyPrices[plan] || null;

  const handleCancelConfirm = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      const res = await fetch(endpoints.payments.cancel, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setCancelError(data?.detail || 'Something went wrong. Please try again.');
        setCancelling(false);
        return;
      }

      await refreshUser();
      setShowCancel(false);
      setCancelSuccess(true);

    } catch {
      setCancelError('Network error. Please try again.');
    }
    setCancelling(false);
  };


  const nextBillingDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="profile-page">
      <div className="profile-wrap">

        <button className="profile-back" onClick={() => navigate('/my-guides')}>
          ← My Guides
        </button>

        <header className="m-page-head">
          <div>
            <p className="m-eyebrow">Settings</p>
            <h1>Account</h1>
          </div>
        </header>

        {/* ── Profile ── */}
        <section className="profile-row">
          <div className="profile-row__label"><h2>Profile</h2></div>
          <div className="profile-row__body profile-id">
            <div className="profile-avatar">
              {getInitials(user)}
              {user.avatar_url && (
                <img src={user.avatar_url} alt="" onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>
            <div className="profile-id__text">
              <div className="profile-id__name">{user.display_name || user.email.split('@')[0]}</div>
              <div className="profile-id__email">{user.email}</div>
            </div>
          </div>
        </section>

        {/* ── Usage ── */}
        <section className="profile-row">
          <div className="profile-row__label"><h2>Usage</h2></div>
          <div className="profile-row__body profile-stats">
            <div>
              <div className="profile-stat m-mono">{user.total_guides_created}</div>
              <div className="m-eyebrow">Guides created</div>
            </div>
            <div>
              <div className={`profile-stat m-mono${user.guides_remaining === 0 ? ' profile-stat--empty' : ''}`}>
                {user.guides_remaining === null ? '∞' : user.guides_remaining}
              </div>
              <div className="m-eyebrow">Guides remaining</div>
            </div>
          </div>
        </section>

        {/* ── Current Plan ── */}
        <section className="profile-row">
          <div className="profile-row__label">
            <h2>Plan</h2>
            <p>What your account can do today.</p>
          </div>
          <div className="profile-row__body">
            <div className="profile-plan">
              <span className={`m-badge ${isPaid ? 'm-badge--public' : 'm-badge--private'}`}>
                {onTrial ? `Free trial · ends ${trialEnd}` : meta.label}
              </span>
              {isPaid && planPrices && (
                <span className="profile-plan__price m-mono">
                  {planPrices.monthly}/mo · {planPrices.yearly}/yr
                </span>
              )}
              {/* FREE_MODE: restore this button when FREE_MODE=false */}
              {!FREE_MODE && !isPaid && (
                <button className="lp-btn sm profile-plan__cta" onClick={() => navigate('/#pricing')}>
                  Upgrade plan
                </button>
              )}
            </div>

            {/* FREE_MODE: remove this block when FREE_MODE=false */}
            {FREE_MODE && (
              <p className="profile-note">
                <strong>All features are free right now.</strong>{' '}
                You have access to every Individual plan feature, no subscription required.
              </p>
            )}

            <ul className="profile-features">
              {meta.features.map((f) => <li key={f}>{f}</li>)}
            </ul>
          </div>
        </section>

        {/* ── Billing (paid plans only) ── */}
        {/* FREE_MODE: restore this check when FREE_MODE=false */}
        {!FREE_MODE && isPaid && planPrices && (
          <section className="profile-row">
            <div className="profile-row__label"><h2>Billing</h2></div>
            <div className="profile-row__body">
              <dl className="profile-dl">
                <Row label="Status">
                  <span className={`profile-status${subscription?.status === 'past_due' ? ' profile-status--due' : ''}`}>
                    {subscription?.status === 'past_due' ? 'Past due' : 'Active'}
                  </span>
                </Row>
                <Row label="Monthly price"><span className="m-mono">{planPrices.monthly}</span></Row>
                <Row label="Yearly price"><span className="m-mono">{planPrices.yearly}</span></Row>
                <Row label="Next billing date">{nextBillingDate || '—'}</Row>
                <Row label="Payment via">
                  {isRazorpay ? 'Razorpay (UPI / Card / Net Banking)' : 'Stripe (Card / Apple Pay)'}
                </Row>
                {subscription?.cancel_at_period_end && (
                  <Row label="Cancellation">
                    <span className="profile-text-danger">Cancels at end of billing period</span>
                  </Row>
                )}
              </dl>
            </div>
          </section>
        )}

        {/* ── Danger Zone ── */}
        <section className="profile-row">
          <div className="profile-row__label"><h2>Subscription</h2></div>
          <div className="profile-row__body">
            {/* FREE_MODE: restore original cancel logic when FREE_MODE=false */}
            {FREE_MODE ? (
              <p className="profile-muted">Features are currently free, so there is no subscription to cancel.</p>
            ) : isPaid && subscription?.cancel_at_period_end ? (
              <div>
                <p className="profile-strong">Cancellation scheduled</p>
                <p className="profile-text-danger profile-small">
                  Your {meta.label} plan will end on{' '}
                  {nextBillingDate || 'the end of your billing period'}. After that your guides keep playing, but you can't create new ones.
                </p>
              </div>
            ) : isPaid ? (
              <div className="profile-cancel">
                <p className="profile-muted">
                  Your access continues until period end, then your guides stay playable but read-only.{plan === 'teams' ? ' Your workspace will be removed.' : ''}
                </p>
                <button className="profile-danger-btn" onClick={() => { setCancelError(''); setShowCancel(true); }}>
                  Cancel subscription
                </button>
              </div>
            ) : (
              <p className="profile-muted">{onTrial ? 'Your free trial needs no card, so there is nothing to cancel.' : 'You have no active subscription, so there is nothing to cancel.'}</p>
            )}
            {cancelError && <p className="profile-text-danger profile-small" role="alert">{cancelError}</p>}
            {cancelSuccess && (
              <p className="profile-note" role="status">
                Subscription cancelled. Your access continues until the end of the billing period.
              </p>
            )}
          </div>
        </section>

        {/* ── Sign out ── */}
        <section className="profile-row profile-row--last">
          <div className="profile-row__label"><h2>Session</h2></div>
          <div className="profile-row__body">
            <button className="lp-btn quiet sm" onClick={() => { logout(); navigate('/login'); }}>
              Sign out
            </button>
          </div>
        </section>

      </div>

      {showCancel && (
        <CancelModal
          plan={plan}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancel(false)}
          loading={cancelling}
        />
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="profile-dl__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
